import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import type { Product } from "../../types";
import { EntityStatus } from "../../types";
import ProductCard from "../common/ProductCard";
import type { UIProduct } from "../../types";
import { primaryProductCategoryName } from "../../utils/explorerUtils";

function productToUIProduct(p: Product): UIProduct {
  const categoryNames = (p.categories ?? []).map((c) => c.name).filter(Boolean);
  return {
    id: p.uuid,
    name: p.name,
    description: p.description || "No description available",
    category: primaryProductCategoryName(p),
    categoryNames,
    tags: p.tags ?? [],
    heroImage: p.heroImage?.name ?? null,
    status: p.status as EntityStatus,
    type: p.type,
  };
}

interface ProductExplorerViewsProps {
  product: Product;
}

export const ProductExplorerCard: React.FC<ProductExplorerViewsProps> = ({
  product,
}) => {
  return <ProductCard product={productToUIProduct(product)} />;
};

export const ProductExplorerListRow: React.FC<ProductExplorerViewsProps> = ({
  product,
}) => {
  const category =
    product.categories?.length > 0
      ? product.categories[0].name
      : "Uncategorized";

  const initials = product.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Box
      component={RouterLink}
      to={`/product/${product.uuid}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
        cursor: "pointer",
        textDecoration: "none",
        color: "inherit",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Avatar sx={{ bgcolor: "primary.light", width: 40, height: 40 }}>
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap>
          {product.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {product.description || "No description"}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
        {category}
      </Typography>
      <Chip
        label={product.status}
        size="small"
        color={product.status === EntityStatus.ACTIVE ? "success" : "default"}
      />
    </Box>
  );
};
